import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/SimpleAuthContext';
import { useToast } from '../contexts/ToastContext';
import analyticsService from '../services/analyticsService';
import API_CONFIG from '../config/apiConfig';
import { getCoursePassportProgressions } from '../config/careerPassports';
import './CourseDetail.css';
import LMSCourseInformation from '../components/courses/LMSCourseInformation';
import { courseDuration } from '../utils/courseDisplay';
import {
  AcademicCapIcon,
  ClockIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  BuildingOfficeIcon,
  StarIcon,
  MapPinIcon,
  BookOpenIcon,
  BriefcaseIcon,
  HeartIcon,
  XCircleIcon,
  BanknotesIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';

const normalizeSaqaText = (value) =>
  (value || '')
    .replace(/\r\n/g, '\n')
    .replace(/\u00a0/g, ' ')
    .trim();

const isListLine = (line) => /^(\d+[\.\)]|[-•*])\s+/.test(line);

const stripListMarker = (line) => line.replace(/^(\d+[\.\)]|[-•*])\s+/, '').trim();

const splitSentences = (text) =>
  text.match(/[^.!?]+[.!?]+|[^.!?]+$/g)?.map((sentence) => sentence.trim()).filter(Boolean) || [text];

const chunkArray = (items, size) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const combineSaqaTexts = (...values) => {
  const uniqueValues = [];

  values.forEach((value) => {
    const normalized = normalizeSaqaText(value);
    if (normalized && !uniqueValues.includes(normalized)) {
      uniqueValues.push(normalized);
    }
  });

  return uniqueValues.join('\n\n');
};

const parseSaqaContent = (value) => {
  const normalized = normalizeSaqaText(value);

  if (!normalized) {
    return [];
  }

  const paragraphBlocks = normalized
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  if (paragraphBlocks.length > 1) {
    return paragraphBlocks.flatMap((paragraph) => parseSaqaContent(paragraph));
  }

  const lines = normalized
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length > 1) {
    const blocks = [];
    let currentParagraph = [];
    let currentList = [];

    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        blocks.push({
          type: 'paragraph',
          text: currentParagraph.join(' ')
        });
        currentParagraph = [];
      }
    };

    const flushList = () => {
      if (currentList.length > 0) {
        blocks.push({
          type: 'list',
          items: [...currentList]
        });
        currentList = [];
      }
    };

    lines.forEach((line) => {
      if (isListLine(line)) {
        flushParagraph();
        currentList.push(stripListMarker(line));
      } else {
        flushList();
        currentParagraph.push(line);
      }
    });

    flushParagraph();
    flushList();

    return blocks.flatMap((block) => {
      if (block.type === 'paragraph' && block.text.length > 360) {
        return chunkArray(splitSentences(block.text), 2).map((sentenceGroup) => ({
          type: 'paragraph',
          text: sentenceGroup.join(' ')
        }));
      }

      return [block];
    });
  }

  if (normalized.length > 360) {
    const sentenceGroups = chunkArray(splitSentences(normalized), 2);
    if (sentenceGroups.length > 1) {
      return sentenceGroups.map((sentenceGroup) => ({
        type: 'paragraph',
        text: sentenceGroup.join(' ')
      }));
    }
  }

  return [{ type: 'paragraph', text: normalized }];
};

const hasMoreSaqaContent = (value) => {
  const normalized = normalizeSaqaText(value);
  const blocks = parseSaqaContent(value);

  if (!normalized) {
    return false;
  }

  return normalized.length > 320 || blocks.length > 2 || blocks.some((block) => block.type === 'list' && block.items.length > 3);
};

const renderSaqaContent = (value, { preview = false } = {}) => {
  const blocks = parseSaqaContent(value);

  if (!blocks.length) {
    return <p className="saqa-empty-state">No information available.</p>;
  }

  const visibleBlocks = preview ? blocks.slice(0, 2) : blocks;

  return visibleBlocks.map((block, index) => {
    if (block.type === 'list') {
      const visibleItems = preview ? block.items.slice(0, 3) : block.items;

      return (
        <ul key={`saqa-list-${index}`} className={`saqa-list${preview ? ' saqa-list--preview' : ''}`}>
          {visibleItems.map((item, itemIndex) => (
            <li key={`saqa-list-${index}-${itemIndex}`} className={`saqa-list-item${preview ? ' saqa-list-item--preview' : ''}`}>
              {item}
            </li>
          ))}
        </ul>
      );
    }

    return (
      <p key={`saqa-paragraph-${index}`} className={`saqa-paragraph${preview ? ' saqa-paragraph--preview' : ''}`}>
        {block.text}
      </p>
    );
  });
};

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, makeAuthenticatedRequest } = useAuth();
  const { showToast } = useToast();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [wishlistChecking, setWishlistChecking] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [wishlistItemId, setWishlistItemId] = useState(null);
  const [lmsActionLoading, setLmsActionLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    modules: true,
    requirements: true,
    careers: true,
    saqa: true
  });

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);
        console.log(`🔄 Fetching course with ID: ${id}`);

        // Use direct backend URL to bypass proxy issues
        const apiUrl = API_CONFIG.courses.detail(id);
        console.log(`🔄 API URL: ${apiUrl}`);

        const response = await axios.get(apiUrl);
        console.log(`✅ Course data received:`, response.data);

        setCourse(response.data);

        // Track page view
        analyticsService.trackPageView(`Course Detail - ${response.data.programme_name}`);
      } catch (err) {
        console.error('❌ Error fetching course:', err);
        console.error('❌ Error details:', {
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          url: err.config?.url,
          baseURL: err.config?.baseURL,
          fullURL: err.config?.baseURL + err.config?.url,
          responseData: err.response?.data
        });
        setError(`Failed to load course details: ${err.response?.status || err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCourse();
    }
  }, [id]);

  useEffect(() => {
    const fetchWishlistStatus = async () => {
      if (!isAuthenticated || !id) {
        setIsWishlisted(false);
        setWishlistItemId(null);
        setWishlistChecking(false);
        return;
      }

      try {
        setWishlistChecking(true);

        const response = await makeAuthenticatedRequest(API_CONFIG.registrations.wishlist());
        if (!response.ok) {
          if (response.status === 401) {
            setIsWishlisted(false);
            setWishlistItemId(null);
          }
          return;
        }

        const wishlistData = await response.json();
        const wishlistItems = wishlistData.results || wishlistData || [];
        const courseId = parseInt(id, 10);
        const wishlistEntry = wishlistItems.find((item) => item.course?.id === courseId);

        setIsWishlisted(!!wishlistEntry);
        setWishlistItemId(wishlistEntry?.id || null);
      } catch (wishlistError) {
        console.error('Error fetching course wishlist status:', wishlistError);
      } finally {
        setWishlistChecking(false);
      }
    };

    fetchWishlistStatus();
  }, [id, isAuthenticated, makeAuthenticatedRequest]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleWishlistToggle = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: `/courses/${id}` } } });
      return;
    }

    if (wishlistLoading || !course) {
      return;
    }

    try {
      setWishlistLoading(true);

      if (isWishlisted) {
        let itemId = wishlistItemId;

        if (!itemId) {
          const listResponse = await makeAuthenticatedRequest(API_CONFIG.registrations.wishlist());
          if (!listResponse.ok) {
            throw new Error('Unable to load wishlist items');
          }

          const listData = await listResponse.json();
          const wishlistItems = listData.results || listData || [];
          const courseId = parseInt(id, 10);
          const wishlistEntry = wishlistItems.find((item) => item.course?.id === courseId);
          itemId = wishlistEntry?.id;
        }

        if (!itemId) {
          showToast('We could not find this course in your wishlist.', 'error');
          return;
        }

        const removeResponse = await makeAuthenticatedRequest(
          `${API_CONFIG.registrations.wishlist()}${itemId}/`,
          { method: 'DELETE' }
        );

        if (removeResponse.ok) {
          setIsWishlisted(false);
          setWishlistItemId(null);
          showToast('Course removed from wishlist', 'success');
          analyticsService.trackWishlistAction('remove', parseInt(id, 10), course.programme_name);
        } else {
          const errorData = await removeResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || errorData.error || 'Failed to remove course from wishlist');
        }
      } else {
        const addResponse = await makeAuthenticatedRequest(API_CONFIG.registrations.wishlist(), {
          method: 'POST',
          body: JSON.stringify({
            course_id: parseInt(id, 10),
            notify_on_opening: true,
            notify_on_capacity: true,
          }),
        });

        if (addResponse.ok) {
          const responseData = await addResponse.json().catch(() => ({}));
          setIsWishlisted(true);
          setWishlistItemId(responseData.id || null);
          showToast('Course added to wishlist', 'success');
          analyticsService.trackWishlistAction('add', parseInt(id, 10), course.programme_name);
        } else {
          const errorData = await addResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || errorData.error || 'Failed to add course to wishlist');
        }
      }
    } catch (wishlistError) {
      console.error('Error toggling wishlist:', wishlistError);
      showToast(wishlistError.message || 'An unexpected error occurred while updating your wishlist.', 'error');
    } finally {
      setWishlistLoading(false);
    }
  };

  const handlePrimaryAction = async () => {
    if (!course) return;

    if (course.is_lms_hosted) {
      if (!isAuthenticated) {
        navigate('/login', { state: { from: { pathname: `/courses/${id}` } } });
        return;
      }

      try {
        setLmsActionLoading(true);
        const response = await makeAuthenticatedRequest(API_CONFIG.lms.acceptEnrollment(), {
          method: 'POST',
          body: JSON.stringify({ course_id: parseInt(id, 10) }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || errorData.error || 'Unable to start LMS handoff');
        }

        const enrollment = await response.json();
        navigate(`/learning/${enrollment.public_id}`, {
          state: {
            enrollment,
            course,
          },
        });
      } catch (actionError) {
        console.error('Failed to start LMS enrollment:', actionError);
        showToast(actionError.message || 'We could not start the LMS handoff just yet.', 'error');
      } finally {
        setLmsActionLoading(false);
      }
      return;
    }

    if (course.external_provider_url) {
      window.open(course.external_provider_url, '_blank', 'noopener,noreferrer');
      return;
    }

    if (course.can_register) {
      navigate(`/apply/${course.id}`);
    }
  };

  if (loading) {
    return (
      <div className="course-detail-container">
        <div className="course-detail-content">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading course details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="course-detail-container">
        <div className="course-detail-content">
          <div className="error-message">
            <XCircleIcon className="icon-lg" />
            <h2>Course Not Found</h2>
            <p>{error || 'The requested course could not be found.'}</p>
            <Link to="/courses" className="btn-primary">
              Back to Courses
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const passportProgressions = getCoursePassportProgressions(course);

  return (
    <div className="course-detail-container">
      {/* Header */}
      <div className="course-detail-header">
        <div className="course-detail-header-content">
          <Link to="/courses" className="course-back-link">
            <ArrowLeftIcon className="icon-sm" />
            Back to Courses
          </Link>

          <div className="course-header-main">
            <div className="course-header-info">
              <div className="course-header-badges">
                {course.nqf_exit_level && <span className="nqf-badge">NQF Level {course.nqf_exit_level}</span>}
                <span className={`course-type-badge ${course.course_type === 'course' ? 'regular-course' : 'short-course'}`}>
                  {course.course_type_display}
                </span>
                {course.is_lms_hosted && (
                  <span className="lms-delivery-badge">LMS</span>
                )}
                {course.accreditation_status === 'approved' && (
                  <span className="accreditation-badge">Accredited</span>
                )}
              </div>

              <h1 className="course-header-title">{course.programme_name}</h1>

              <div className="course-header-meta">
                <div className="course-meta-item">
                  <MapPinIcon className="icon-sm" />
                  <span>{course.is_lms_hosted ? 'LMS Online' : `${course.campus?.name || 'Campus unavailable'}${course.campus?.city ? `, ${course.campus.city}` : ''}`}</span>
                </div>
                <div className="course-meta-item">
                  <ClockIcon className="icon-sm" />
                  <span>{courseDuration(course)}</span>
                </div>
                {course.qualification_credits && <div className="course-meta-item">
                  <StarIcon className="icon-sm" />
                  <span>{course.qualification_credits} credits</span>
                </div>}
              </div>

              <div className="course-header-passports" aria-label="Passport tracks">
                {passportProgressions.map(progress => <span key={progress.id} className={`course-passport-token course-passport-token--${progress.tone}`}>{progress.title}</span>)}
              </div>
            </div>

            <div className="course-header-actions">
              {isAuthenticated ? (
                <button
                  className={`btn-wishlist-detail ${isWishlisted ? 'wishlisted' : ''}`}
                  onClick={handleWishlistToggle}
                  disabled={wishlistLoading || wishlistChecking}
                  aria-pressed={isWishlisted}
                >
                  {isWishlisted ? (
                    <HeartSolidIcon className="icon-sm" />
                  ) : (
                    <HeartIcon className="icon-sm" />
                  )}
                  {wishlistChecking
                    ? 'Checking Wishlist...'
                    : wishlistLoading
                      ? (isWishlisted ? 'Removing...' : 'Saving...')
                      : (isWishlisted ? 'Wishlisted' : 'Add to Wishlist')}
                </button>
              ) : (
                <Link
                  to="/login"
                  state={{ from: { pathname: `/courses/${course.id}` } }}
                  className="btn-wishlist-detail"
                >
                  <HeartIcon className="icon-sm" />
                  Login to Wishlist
                </Link>
              )}
              
              {course.is_lms_hosted ? (
                <button
                  type="button"
                  onClick={handlePrimaryAction}
                  className="btn-course-apply-header"
                  disabled={lmsActionLoading}
                >
                  <ArrowTopRightOnSquareIcon className="icon-sm" />
                  {lmsActionLoading ? 'Starting LMS handoff...' : 'Accept & Learn'}
                </button>
              ) : course.external_provider_url ? (
                <a
                  href={course.external_provider_url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-course-apply-header"
                >
                  <ArrowTopRightOnSquareIcon className="icon-sm" />
                  Visit Provider
                </a>
              ) : course.can_register ? (
                <Link to={`/apply/${course.id}`} className="btn-course-apply-header">
                  <CheckCircleIcon className="icon-sm" />
                  Apply Now
                </Link>
              ) : (
                <button className="btn-course-full-header" disabled>
                  <XCircleIcon className="icon-sm" />
                  {course.is_full ? 'Course Full' : 'Registration Closed'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="course-detail-content">
        <div className="course-content-grid">
          <div className="course-main-content">
            {/* Course Description */}
            <div className="course-section">
              <div className="course-section-header">
                <div className="course-section-icon">
                  <BookOpenIcon className="icon-lg" />
                </div>
                <div>
                  <h2 className="course-section-title">Course Overview</h2>
                  <p className="course-section-subtitle">Learn what this course covers</p>
                </div>
              </div>
              <div className="course-section-content">
                <p>{course.description || 'No course description is available for this programme yet.'}</p>
              </div>
            </div>

            {course.is_lms_hosted && <LMSCourseInformation course={course} />}

              <div className="course-passport-panel" aria-label="Passport track and standard">
                <div className="course-passport-panel-heading">
                  <ShieldCheckIcon className="icon" />
                  <div>
                    <span>Passport track and progression</span>
                    <strong>How this course fits your skills pathway</strong>
                  </div>
                </div>

                {passportProgressions.length > 0 ? passportProgressions.map(progress => (
                  <div className="course-passport-progression" key={progress.id}>
                    <div className="course-passport-progression-topline">
                      <span className={`course-passport-token course-passport-token--${progress.tone}`}>
                        {progress.title}
                      </span>
                      <span className="course-passport-level">
                        {progress.progressionLabel || progress.targetLabel}
                      </span>
                    </div>
                    <p>{progress.coverage}</p>
                    <div className="course-passport-progression-meta">
                      <span>{progress.nqfLevel ? `NQF Level ${progress.nqfLevel}` : 'NQF route pending'}</span>
                      <span>{progress.alignment_source === 'staff_confirmed'
                        ? (progress.standardLabel || 'Course mapping confirmed')
                        : 'Suggested pathway · staff review required'}</span>
                    </div>
                    <small>{progress.progressionNote}</small>
                  </div>
                )) : (
                  <div className="course-passport-pending">
                    <strong>{course.passport_alignment_reviewed ? 'No Passport track linked' : 'Passport mapping needed'}</strong>
                    <span>{course.passport_alignment_reviewed
                      ? 'This course has been reviewed and is not currently aligned to a Passport.'
                      : 'This course still needs a confirmed Passport track and progression standard.'}</span>
                  </div>
                )}
              </div>

            {/* Modules Section */}
            {course.modules && course.modules.length > 0 && (
              <ModulesSection 
                modules={course.modules}
                expanded={expandedSections.modules}
                onToggle={() => toggleSection('modules')}
              />
            )}

            {/* Requirements Section */}
            {course.requirements && course.requirements.length > 0 && (
              <RequirementsSection 
                requirements={course.requirements}
                expanded={expandedSections.requirements}
                onToggle={() => toggleSection('requirements')}
              />
            )}

            {/* Career Information Section */}
            {course.career_info && (
              <CareerSection
                careerInfo={course.career_info}
                expanded={expandedSections.careers}
                onToggle={() => toggleSection('careers')}
              />
            )}

            {/* SAQA Information Section */}
            {(course.saqa_purpose || course.saqa_learning_outcomes || course.saqa_entry_requirements) && (
              <SAQASection
                course={course}
                expanded={expandedSections.saqa}
                onToggle={() => toggleSection('saqa')}
              />
            )}
          </div>

          {/* Sidebar */}
          <div className="course-sidebar">
            <QuickFactsSidebar course={course} />
            <ApplicationSidebar course={course} onPrimaryAction={handlePrimaryAction} lmsActionLoading={lmsActionLoading} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Component for Modules Section
const ModulesSection = ({ modules, expanded, onToggle }) => (
  <div className="course-section">
    <div className="course-section-header" onClick={onToggle} style={{cursor: 'pointer'}}>
      <div className="course-section-icon">
        <AcademicCapIcon className="icon-lg" />
      </div>
      <div style={{flex: 1}}>
        <h2 className="course-section-title">Course Modules</h2>
        <p className="course-section-subtitle">{modules.length} modules covering all aspects</p>
      </div>
      {expanded ? (
        <ChevronUpIcon className="icon" />
      ) : (
        <ChevronDownIcon className="icon" />
      )}
    </div>
    {expanded && (
      <div className="course-section-content">
        <div className="modules-grid">
          {modules.map((module, index) => (
              <div key={module.id} className="module-card">
                <div className="module-header">
                  <span className="module-number">{index + 1}</span>
                  <div className="module-meta">
                  <span className="module-duration">{module.duration || 'Duration TBC'}</span>
                  <span className="module-credits">{module.credits ? `${module.credits} credits` : 'Credits TBC'}</span>
                  </div>
                </div>
                <h3 className="module-title">{module.title}</h3>
              <p className="module-description">{module.description || 'Module details will be shared by the course team.'}</p>
              {Array.isArray(module.topics) && module.topics.length > 0 && (
                <div className="module-topics">
                  <h4>Topics Covered:</h4>
                  <ul>
                    {module.topics.map((topic, topicIndex) => (
                      <li key={topicIndex}>{topic}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

// Component for Requirements Section
const RequirementsSection = ({ requirements, expanded, onToggle }) => {
  // Group requirements by category
  const groupedRequirements = (Array.isArray(requirements) ? requirements : []).reduce((acc, req) => {
    const categoryName = req.category?.name || 'General';
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(req);
    return acc;
  }, {});

  return (
    <div className="course-section">
      <div className="course-section-header" onClick={onToggle} style={{cursor: 'pointer'}}>
        <div className="course-section-icon">
          <CheckCircleIcon className="icon-lg" />
        </div>
        <div style={{flex: 1}}>
          <h2 className="course-section-title">Entry Requirements</h2>
          <p className="course-section-subtitle">What you need to apply</p>
        </div>
        {expanded ? (
          <ChevronUpIcon className="icon" />
        ) : (
          <ChevronDownIcon className="icon" />
        )}
      </div>
      {expanded && (
        <div className="course-section-content">
          {Object.entries(groupedRequirements).map(([categoryName, categoryRequirements]) => (
            <div key={categoryName} className="requirements-category">
              <h3 className="requirements-category-title">{categoryName}</h3>
              <div className="requirements-list">
                {categoryRequirements.map((req) => (
                  <div key={req.id} className="requirement-item">
                    <div className="requirement-header">
                      <span className="requirement-label">{req.label || 'Requirement'}</span>
                      {req.is_required ? (
                        <span className="requirement-badge required">Required</span>
                      ) : (
                        <span className="requirement-badge optional">Optional</span>
                      )}
                    </div>
                    <p className="requirement-description">{req.description || 'No additional details provided.'}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Component for Career Section
const CareerSection = ({ careerInfo, expanded, onToggle }) => (
  <div className="course-section">
    <div className="course-section-header" onClick={onToggle} style={{cursor: 'pointer'}}>
      <div className="course-section-icon">
        <BriefcaseIcon className="icon-lg" />
      </div>
      <div style={{flex: 1}}>
        <h2 className="course-section-title">Career Opportunities</h2>
        <p className="course-section-subtitle">Your future career prospects</p>
      </div>
      {expanded ? (
        <ChevronUpIcon className="icon" />
      ) : (
        <ChevronDownIcon className="icon" />
      )}
    </div>
    {expanded && (
      <div className="course-section-content">
        <p className="career-overview">{careerInfo.overview}</p>

        {Array.isArray(careerInfo.roles) && careerInfo.roles.length > 0 && (
          <div className="career-roles">
            <h3>Career Roles</h3>
            <div className="roles-grid">
              {careerInfo.roles.map((role) => (
                <div key={role.id} className="role-card">
                  <h4 className="role-title">{role.title}</h4>
                  <p className="role-description">{role.description}</p>
                  <div className="role-details">
                    <div className="role-salary">
                      <BanknotesIcon className="icon-sm" />
                      <span>{role.salary_range}</span>
                    </div>
                    <div className="role-growth">
                      <span className="growth-indicator">{role.growth_prospects}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {Array.isArray(careerInfo.sectors) && careerInfo.sectors.length > 0 && (
          <div className="career-sectors">
            <h3>Industry Sectors</h3>
            <div className="sectors-list">
              {careerInfo.sectors.map((sector, index) => (
                <span key={index} className="sector-tag">{sector}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    )}
  </div>
);

// Component for Quick Facts Sidebar
const QuickFactsSidebar = ({ course }) => (
  <div className="course-sidebar-section">
    <div className="sidebar-section-header">
      <h3 className="sidebar-section-title">Quick Facts</h3>
    </div>
    <div className="sidebar-section-content">
      <div className="quick-facts-grid">
        {(!course.is_lms_hosted || course.nqf_exit_level) && <div className="quick-fact-item">
          <div className="quick-fact-icon">
            <AcademicCapIcon className="icon-sm" />
          </div>
          <div className="quick-fact-content">
            <span className="quick-fact-label">NQF Level</span>
            <span className="quick-fact-value">{course.nqf_exit_level || 'Not specified'}</span>
          </div>
        </div>}

        {(!course.is_lms_hosted || Boolean(course.qualification_credits)) && <div className="quick-fact-item">
          <div className="quick-fact-icon">
            <StarIcon className="icon-sm" />
          </div>
          <div className="quick-fact-content">
            <span className="quick-fact-label">Credits</span>
            <span className="quick-fact-value">{course.qualification_credits || 'Not specified'}</span>
          </div>
        </div>}

        <div className="quick-fact-item">
          <div className="quick-fact-icon">
            <ClockIcon className="icon-sm" />
          </div>
          <div className="quick-fact-content">
            <span className="quick-fact-label">Duration</span>
            <span className="quick-fact-value">{courseDuration(course)}</span>
          </div>
        </div>

        <div className="quick-fact-item">
          <div className="quick-fact-icon">
            <UserGroupIcon className="icon-sm" />
          </div>
          <div className="quick-fact-content">
            <span className="quick-fact-label">{course.is_lms_hosted ? 'Learning mode' : 'Available places'}</span>
            <span className="quick-fact-value">{course.is_lms_hosted ? ({self_paced: 'Self-paced', facilitated: 'Facilitated', blended: 'Blended'}[course.lms_course_details?.modality] || 'To be confirmed') : `${course.available_capacity ?? 'TBC'}/${course.max_capacity ?? 'TBC'}`}</span>
          </div>
        </div>

        {course.stipend_amount && course.stipend_amount > 0 && (
          <div className="quick-fact-item">
            <div className="quick-fact-icon">
              <BanknotesIcon className="icon-sm" />
            </div>
          <div className="quick-fact-content">
            <span className="quick-fact-label">Monthly Allowance</span>
            <span className="quick-fact-value">R {course.stipend_amount.toLocaleString()}</span>
          </div>
        </div>
        )}
      </div>
    </div>
  </div>
);

// Component for Application Sidebar
const ApplicationSidebar = ({ course, onPrimaryAction, lmsActionLoading }) => (
  <div className={`course-sidebar-section ${course.is_lms_hosted ? 'lms-application-sidebar' : ''}`}>
    <div className="sidebar-section-header">
      <h3 className="sidebar-section-title">{course.is_lms_hosted ? 'Start learning' : 'Application'}</h3>
    </div>
    <div className="sidebar-section-content">
      <div className="application-section">
        {course.is_lms_hosted ? (
          <div className="application-available">
            <div className="application-status">
              <div className="application-status-icon-large">
                <ArrowTopRightOnSquareIcon className="icon-lg text-blue" />
              </div>
              <div className="application-status-content">
                <h4 className="application-status-title">Start learning on the LMS</h4>
                <p className="application-status-subtitle">
                  Open this course through a secure LMS handoff. Your learning progress syncs back to Career Guidance.
                </p>
              </div>
            </div>
            <div className="application-actions">
              <button
                type="button"
                onClick={onPrimaryAction}
                className="btn-apply-sidebar"
                disabled={lmsActionLoading}
              >
                <ArrowTopRightOnSquareIcon className="icon-sm" />
                {lmsActionLoading ? 'Starting LMS handoff...' : 'Accept & Learn'}
              </button>
            </div>
          </div>
        ) : course.external_provider_url ? (
          <div className="application-available">
            <div className="application-status">
              <div className="application-status-icon-large">
                <ArrowTopRightOnSquareIcon className="icon-lg text-blue" />
              </div>
              <div className="application-status-content">
                <h4 className="application-status-title">External learning provider</h4>
                <p className="application-status-subtitle">
                  This course is hosted with a partner provider outside the Career Guidance app.
                </p>
              </div>
            </div>
            <div className="application-actions">
              <a href={course.external_provider_url} target="_blank" rel="noreferrer" className="btn-apply-sidebar">
                <ArrowTopRightOnSquareIcon className="icon-sm" />
                Visit Provider
              </a>
            </div>
          </div>
        ) : course.can_register ? (
          <div className="application-available">
            <div className="application-status">
              <div className="application-status-icon-large">
                <CheckCircleIcon className="icon-lg text-green" />
              </div>
              <div className="application-status-content">
                <h4 className="application-status-title">Applications Open</h4>
                <p className="application-status-subtitle">
                  You can apply for this course now. {course.available_capacity ?? 'Limited'} spots available.
                </p>
              </div>
            </div>
            <div className="application-actions">
              <Link to={`/apply/${course.id}`} className="btn-apply-sidebar">
                <CheckCircleIcon className="icon-sm" />
                Apply Now
              </Link>
            </div>
          </div>
        ) : (
          <div className="application-unavailable">
            <div className="application-status">
              <div className="application-status-icon-large">
                <ExclamationTriangleIcon className="icon-lg text-yellow" />
              </div>
              <div className="application-status-content">
                <h4 className="application-status-title">
                  {course.is_full ? 'Course Full' : 'Registration Closed'}
                </h4>
                <p className="application-status-subtitle">
                  {course.is_full
                    ? 'This course has reached maximum capacity.'
                    : 'Registration for this course is currently closed.'
                  }
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);

// Component for SAQA Information Section
const SAQACard = ({ icon: Icon, title, subtitle, text, expanded, onToggle }) => {
  if (!text) return null;
  const showToggle = hasMoreSaqaContent(text);

  return (
    <section className="saqa-detail-card">
      <div className="saqa-detail-card-header">
        <div className="saqa-detail-card-icon">
          <Icon className="icon-sm" />
        </div>
        <div>
          <h4 className="saqa-detail-card-title">{title}</h4>
          {subtitle && <p className="saqa-detail-card-subtitle">{subtitle}</p>}
        </div>
      </div>
      <div className="saqa-detail-card-body">
        {renderSaqaContent(text, { preview: !expanded && showToggle })}
      </div>
      {showToggle && (
        <div className="saqa-detail-card-footer">
          <button type="button" className="saqa-card-toggle" onClick={onToggle}>
            {expanded ? 'Show less' : 'More'}
            {expanded ? <ChevronUpIcon className="icon-xs" /> : <ChevronDownIcon className="icon-xs" />}
          </button>
        </div>
      )}
    </section>
  );
};

const SAQASection = ({ course, expanded, onToggle }) => {
  const [expandedCards, setExpandedCards] = useState({});

  useEffect(() => {
    setExpandedCards({});
  }, [course?.id]);

  const saqaCards = [
    {
      key: 'purpose',
      icon: DocumentTextIcon,
      title: 'Purpose & Rationale',
      subtitle: 'Why this qualification exists',
      text: combineSaqaTexts(course.saqa_purpose, course.saqa_qualification_rationale)
    },
    {
      key: 'outcomes',
      icon: AcademicCapIcon,
      title: 'Exit Level Outcomes',
      subtitle: 'What learners should be able to do',
      text: course.saqa_learning_outcomes
    },
    {
      key: 'competencies',
      icon: CheckCircleIcon,
      title: 'Skills & Competencies',
      subtitle: 'Knowledge and capabilities developed',
      text: course.saqa_competencies
    },
    {
      key: 'careers',
      icon: BriefcaseIcon,
      title: 'Career Opportunities',
      subtitle: 'Potential roles after completion',
      text: course.saqa_career_opportunities
    },
    {
      key: 'entry',
      icon: UserGroupIcon,
      title: 'Entry Requirements',
      subtitle: 'Minimum admission requirements',
      text: course.saqa_entry_requirements
    },
    {
      key: 'assessment',
      icon: StarIcon,
      title: 'Assessment Criteria',
      subtitle: 'How progress is evaluated',
      text: course.saqa_assessment_criteria
    },
    {
      key: 'credits',
      icon: BanknotesIcon,
      title: 'Credit Structure',
      subtitle: 'How credits are allocated',
      text: course.saqa_credit_breakdown
    },
    {
      key: 'articulation',
      icon: ArrowTopRightOnSquareIcon,
      title: 'Career Progression Pathways',
      subtitle: 'Vertical and horizontal progression routes',
      text: course.saqa_articulation_pathways
    },
    {
      key: 'rpl',
      icon: ShieldCheckIcon,
      title: 'Recognition of Prior Learning',
      subtitle: 'How prior experience can count',
      text: course.saqa_recognition_pathways
    },
    {
      key: 'quality',
      icon: ShieldCheckIcon,
      title: 'Quality Assurance',
      subtitle: 'Standards and moderation requirements',
      text: course.saqa_quality_assurance
    },
    {
      key: 'provider',
      icon: BuildingOfficeIcon,
      title: 'Provider Requirements',
      subtitle: 'Accreditation requirements for providers',
      text: course.saqa_provider_requirements
    },
    {
      key: 'international',
      icon: ArrowTopRightOnSquareIcon,
      title: 'International Recognition',
      subtitle: 'Global standards and comparability',
      text: course.saqa_international_comparability
    },
  ].filter((card) => card.text);

  const toggleCard = (cardKey) => {
    setExpandedCards((prev) => ({
      ...prev,
      [cardKey]: !prev[cardKey]
    }));
  };

  return (
    <div className="course-section">
      <div className="course-section-header" onClick={onToggle} style={{ cursor: 'pointer' }}>
        <div className="course-section-icon">
          <DocumentTextIcon className="icon-lg" />
        </div>
        <div style={{ flex: 1 }}>
          <h2 className="course-section-title">SAQA Qualification Information</h2>
          <p className="course-section-subtitle">Structured qualification details from SAQA</p>
        </div>
        {expanded ? <ChevronUpIcon className="icon" /> : <ChevronDownIcon className="icon" />}
      </div>

      {expanded && (
        <div className="course-section-content">
          <div className="saqa-info-grid">
            <div className="saqa-quick-facts">
              <h3>Qualification Overview</h3>
              <div className="saqa-facts-grid">
                {course.saqa_id && (
                  <div className="saqa-fact">
                    <span className="saqa-fact-label">SAQA ID</span>
                    <span className="saqa-fact-value">{course.saqa_id}</span>
                  </div>
                )}
                {course.qualification_credits && (
                  <div className="saqa-fact">
                    <span className="saqa-fact-label">Credits</span>
                    <span className="saqa-fact-value">{course.qualification_credits}</span>
                  </div>
                )}
                {course.nqf_exit_level && (
                  <div className="saqa-fact">
                    <span className="saqa-fact-label">NQF Level</span>
                    <span className="saqa-fact-value">{course.nqf_exit_level}</span>
                  </div>
                )}
                {course.qualification_type && (
                  <div className="saqa-fact">
                    <span className="saqa-fact-label">Type</span>
                    <span className="saqa-fact-value">{course.qualification_type}</span>
                  </div>
                )}
              </div>

              {course.saqa_url && (
                <div className="saqa-link">
                  <a href={course.saqa_url} target="_blank" rel="noopener noreferrer" className="saqa-official-link">
                    View Official SAQA Page
                    <ArrowTopRightOnSquareIcon className="icon-xs" />
                  </a>
                </div>
              )}
            </div>

            <div className="saqa-details-grid">
              {saqaCards.map((card) => (
                <SAQACard
                  key={card.key}
                  {...card}
                  expanded={!!expandedCards[card.key]}
                  onToggle={() => toggleCard(card.key)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseDetail;

